import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import { 
  Briefcase, 
  CheckCircle2, 
  ArrowRight,
  Clock,
  FileText,
  Shield,
  ChevronDown,
  Target,
  FileCheck,
  Calendar,
  Edit,
  Lock,
  Euro,
  AlertTriangle,
  Hammer,
  Package,
  Wrench,
  Star,
  Zap,
  Upload,
  Cpu,
  List,
  ClipboardList,
  ArrowLeft,
  Menu,
  X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const SyntheseRapidePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Document types data
  const documentTypes = [
    {
      id: "rc",
      badge: "RC",
      fullName: "Règlement de Consultation",
      title: "Décryptez votre RC en un coup d'oeil",
      description: "Le Règlement de Consultation fixe les règles du jeu de l'appel d'offres. DCE Express identifie d'un coup les critères qui feront gagner ou perdre votre offre.",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-700",
      extractItems: [
        { icon: Target, text: "Critères de jugement et leur pondération exacte" },
        { icon: FileCheck, text: "Liste des pièces à fournir (DC1, DC2, mémoire technique, références...)" },
        { icon: Calendar, text: "Date et heure limite de remise des offres" },
        { icon: Edit, text: "Modalités de dépôt (dématérialisé, plateforme, format)" },
        { icon: ArrowRight, text: "Variantes autorisées et conditions" },
        { icon: Lock, text: "Conditions d'accès et niveau de qualification exigé" }
      ],
      time: "28 s"
    },
    {
      id: "ccap",
      badge: "CCAP",
      fullName: "Cahier des Clauses Administratives Particulières",
      title: "Repérez les clauses financières à risque",
      description: "Le CCAP contient les clauses qui engagent financièrement votre entreprise pour toute la durée du marché. Une clause mal lue peut coûter très cher.",
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-700",
      extractItems: [
        { icon: Euro, text: "Conditions de prix : ferme, révisable, formule paramétrique" },
        { icon: Clock, text: "Délais contractuels et jalons intermédiaires" },
        { icon: AlertTriangle, text: "Pénalités de retard : taux, plafond, mode de calcul" },
        { icon: Lock, text: "Garanties financières : retenue, caution, avance forfaitaire" },
        { icon: Shield, text: "Obligations d'assurance et montants minimaux" },
        { icon: FileText, text: "Conditions de résiliation et motifs possibles" }
      ],
      time: "31 s"
    },
    {
      id: "cctp",
      badge: "CCTP",
      fullName: "Cahier des Clauses Techniques Particulières",
      title: "Maîtrisez les exigences techniques d'un coup",
      description: "Le CCTP définit le niveau technique attendu. DCE Express extrait les normes, les matériaux imposés et les livrables pour que vous sachiez exactement ce qu'on vous demande.",
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      textColor: "text-emerald-700",
      extractItems: [
        { icon: Wrench, text: "Spécifications techniques détaillées et performances attendues" },
        { icon: FileText, text: "Normes et DTU de référence à respecter" },
        { icon: Hammer, text: "Matériaux ou équipements imposés ou exclus" },
        { icon: Target, text: "Essais, contrôles et vérifications à prévoir" },
        { icon: Package, text: "Exigences environnementales (labels, recyclage, bilan carbone)" },
        { icon: ClipboardList, text: "Livrables techniques attendus à chaque étape" }
      ],
      time: "26 s"
    }
  ];

  // FAQ data
  const faqItems = [
    {
      question: "Comment obtenir rapidement la synthèse d'un Règlement de Consultation ?",
      answer: "Déposez votre RC en PDF dans DCE Express, sélectionnez le type \"RC\" et le domaine du marché. En 28 secondes, vous obtenez les critères de sélection avec leur pondération, la liste complète des pièces à fournir, la date limite et les conditions de participation. Plus besoin de lire le document en entier pour décider si vous répondez."
    },
    {
      question: "Quels points clés ressort l'analyse automatique d'un CCAP ?",
      answer: "L'analyse IA d'un CCAP se concentre sur les clauses à impact financier direct : nature et révision du prix, délais contractuels, taux et plafond des pénalités de retard, retenue de garantie, avance forfaitaire, obligations d'assurance et conditions de résiliation. Les alertes sont mises en évidence séparément pour que vous ne manquiez aucun risque."
    },
    {
      question: "L'outil analyse-t-il les CCTP de tous les secteurs d'activité ?",
      answer: "Oui. DCE Express analyse les CCTP de marchés de travaux (BTP, second oeuvre, VRD), de fournitures (matériels, équipements, consommables) et de services (prestations intellectuelles, maintenance, nettoyage, informatique). Vous sélectionnez votre domaine avant l'analyse et l'IA calibre l'extraction en conséquence."
    },
    {
      question: "Combien de temps pour analyser les 3 pièces d'un DCE complet ?",
      answer: "Chaque pièce est analysée en moins de 30 secondes indépendamment. Pour un DCE complet (RC + CCAP + CCTP), comptez moins de 90 secondes au total, en les déposant les uns après les autres. Vous passez de 3 heures de lecture à moins de 2 minutes de qualification."
    },
    {
      question: "La synthèse rapide est-elle suffisante pour répondre à un appel d'offres ?",
      answer: "La synthèse rapide de DCE Express est conçue pour la qualification : décider rapidement si le marché vous correspond, identifier les risques majeurs et préparer votre réponse. Pour les marchés à fort enjeu financier, elle complète — sans remplacer — une lecture approfondie du juriste ou du responsable technique de votre entreprise."
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      initials: "PL",
      name: "Pierre L.",
      role: "Chargé AO · Entreprise électricité · 60 sal.",
      quote: "J'analyse maintenant un RC en 30 secondes et je sais immédiatement si notre qualification correspond. Avant, je perdais une heure pour arriver à la même conclusion.",
      stars: 5
    },
    {
      initials: "NC",
      name: "Nathalie C.",
      role: "Directrice juridique · ETI services · 210 sal.",
      quote: "Le module CCAP est bluffant. Il a repéré une clause de pénalité sans plafond sur un marché de 400 k€. On a pu renégocier avant signature. Ça vaut son pesant d'or.",
      stars: 5
    },
    {
      initials: "AM",
      name: "Antoine M.",
      role: "Responsable commercial · PME médical · 45 sal.",
      quote: "La synthèse du CCTP m'a sauvé sur un marché de fournitures médicales. Les normes EN obligatoires étaient enfouies page 34. DCE Express les a sorties en première ligne.",
      stars: 4
    }
  ];

  // How it works steps
  const howItWorksSteps = [
    {
      num: "01",
      title: "Choisissez la pièce",
      desc: "Sélectionnez le type de document à analyser : Règlement de Consultation, CCAP ou CCTP. L'IA adapte son analyse en conséquence."
    },
    {
      num: "02",
      title: "Déposez votre PDF",
      desc: "Glissez ou importez votre document. Aucune limite de pages. Tous les DCE au format PDF sont acceptés, quelle que soit leur taille."
    },
    {
      num: "03",
      title: "L'IA lit à votre place",
      desc: "Notre intelligence artificielle parcourt l'intégralité du document, identifie les sections clés et extrait les informations structurantes."
    },
    {
      num: "04",
      title: "Lisez votre synthèse",
      desc: "En moins de 30 secondes : résumé général, points clés numérotés, alertes sur les clauses à risque, et actions recommandées pour votre réponse."
    }
  ];

  return (
    <>
      <SEO 
        title="Synthèse IA de Documents de Marchés Publics"
        description="Analysez vos RC, CCAP et CCTP en 30 secondes grâce à l'Intelligence Artificielle. Synthèse automatique, points clés et alertes sur les clauses à risque."
        path="/analyse-dce"
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-slate-900">Mon DCE Express</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-slate-600 hover:text-violet-700 transition-colors">Accueil</Link>
              <a href="#comment-ca-marche" className="text-slate-600 hover:text-violet-700 transition-colors">Comment ça marche</a>
              <Link to="/blog" className="text-slate-600 hover:text-violet-700 transition-colors">Blog</Link>
              {user ? (
                <Button 
                  onClick={() => navigate("/synthese-marches")}
                  className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-6"
                  data-testid="nav-analyze-btn"
                >
                  Analyser une pièce
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate("/login")}
                    className="text-slate-700"
                    data-testid="nav-login-btn"
                  >
                    Connexion
                  </Button>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-6"
                    data-testid="nav-register-btn"
                  >
                    S'inscrire
                  </Button>
                </div>
              )}
            </div>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <div className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Accueil</Link>
                <a href="#comment-ca-marche" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Comment ça marche</a>
                <Link to="/analyse-gratuite" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Analyse Gratuite</Link>
                <Link to="/blog" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/guide-appels-offres" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Guide</Link>
                <Link to="/contact" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <div className="border-t border-slate-200 mt-2 pt-4 px-4">
                  {user ? (
                    <Button 
                      onClick={() => { navigate("/synthese-marches"); setMobileMenuOpen(false); }}
                      className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-full"
                    >
                      Analyser une pièce
                    </Button>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}
                        className="w-full"
                      >
                        Connexion
                      </Button>
                      <Button 
                        onClick={() => { navigate("/register"); setMobileMenuOpen(false); }}
                        className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-full"
                      >
                        S'inscrire gratuitement
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-violet-600 transition-colors">Accueil</Link>
            <span>/</span>
            <span className="text-slate-700">Synthèse rapide RC · CCAP · CCTP</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4 bg-violet-100 text-violet-700 hover:bg-violet-100">
                Synthèse rapide · Pièces de marché
              </Badge>
              
              <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
                Synthèse <span className="italic text-violet-600">rapide</span> de vos
                <div className="flex flex-wrap gap-3 mt-3">
                  <Badge className="px-4 py-2 text-lg bg-blue-100 text-blue-700 font-semibold">RC</Badge>
                  <Badge className="px-4 py-2 text-lg bg-amber-100 text-amber-700 font-semibold">CCAP</Badge>
                  <Badge className="px-4 py-2 text-lg bg-emerald-100 text-emerald-700 font-semibold">CCTP</Badge>
                </div>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Déposez n'importe quelle pièce de votre DCE et obtenez en moins de 30 secondes les points essentiels, les délais, les alertes financières et les actions à mener — sans lire 200 pages.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Button 
                  size="lg"
                  onClick={() => user ? navigate("/synthese-marches") : navigate("/analyse-gratuite")}
                  className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-analyze-btn"
                >
                  Analyser une pièce
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('comment-ca-marche').scrollIntoView({ behavior: 'smooth' })}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full px-8 py-6 text-lg"
                >
                  Voir comment ça marche
                </Button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-display font-bold text-violet-600">30 s</div>
                  <div className="text-sm text-slate-500">Par pièce analysée</div>
                </div>
                <div>
                  <div className="text-3xl font-display font-bold text-violet-600">3</div>
                  <div className="text-sm text-slate-500">Types de pièces couverts</div>
                </div>
                <div>
                  <div className="text-3xl font-display font-bold text-violet-600">94</div>
                  <div className="text-sm text-slate-500">PME utilisatrices</div>
                </div>
              </div>
            </motion.div>

            {/* Right content - Tri-cards */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="space-y-4">
                {/* RC Card */}
                <Card className="border-l-4 border-l-blue-500 bg-white shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-5">
                    <Badge className="mb-2 bg-blue-100 text-blue-700 text-xs">RC · Règlement de Consultation</Badge>
                    <h3 className="font-semibold text-slate-900 mb-2">Critères & conditions de participation</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="outline" className="text-xs">Critères pondérés</Badge>
                      <Badge variant="outline" className="text-xs">Pièces DC1/DC2</Badge>
                      <Badge variant="outline" className="text-xs">Date limite dépôt</Badge>
                      <Badge variant="outline" className="text-xs">Variantes</Badge>
                    </div>
                    <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      28 s
                    </div>
                  </CardContent>
                </Card>

                {/* CCAP Card */}
                <Card className="border-l-4 border-l-amber-500 bg-white shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-5">
                    <Badge className="mb-2 bg-amber-100 text-amber-700 text-xs">CCAP · Clauses Administratives</Badge>
                    <h3 className="font-semibold text-slate-900 mb-2">Prix, délais, pénalités & garanties</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="outline" className="text-xs">Révision prix</Badge>
                      <Badge variant="outline" className="text-xs">Pénalités retard</Badge>
                      <Badge variant="outline" className="text-xs">Retenue garantie</Badge>
                      <Badge variant="outline" className="text-xs">Avance</Badge>
                    </div>
                    <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      31 s
                    </div>
                  </CardContent>
                </Card>

                {/* CCTP Card */}
                <Card className="border-l-4 border-l-emerald-500 bg-white shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-5">
                    <Badge className="mb-2 bg-emerald-100 text-emerald-700 text-xs">CCTP · Clauses Techniques</Badge>
                    <h3 className="font-semibold text-slate-900 mb-2">Spécifications, normes & livrables</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="outline" className="text-xs">Normes DTU/NF</Badge>
                      <Badge variant="outline" className="text-xs">Matériaux imposés</Badge>
                      <Badge variant="outline" className="text-xs">Contrôles requis</Badge>
                      <Badge variant="outline" className="text-xs">Livrables</Badge>
                    </div>
                    <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      26 s
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reassurance Bar */}
      <section className="py-6 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-white">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-400"></div>
              Sans inscription requise
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400"></div>
              PDF accepté · toutes tailles
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
              Hébergement France · RGPD
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
              Travaux · Fournitures · Services
            </div>
          </div>
        </div>
      </section>

      {/* Document Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-slate-100 text-slate-700">Ce que DCE Express analyse</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Une synthèse <span className="italic text-violet-600">dédiée</span> pour chaque pièce
            </h2>
            <p className="text-slate-600 text-lg max-w-3xl mx-auto">
              Chaque document de marché public a ses propres enjeux. DCE Express adapte son analyse à la nature de la pièce pour ne jamais passer à côté d'un point critique.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {documentTypes.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full border-t-4 ${doc.borderColor} hover:shadow-xl transition-all`}>
                  <CardContent className="p-6">
                    <Badge className={`mb-3 ${doc.bgColor} ${doc.textColor}`}>{doc.badge}</Badge>
                    <p className="text-xs text-slate-500 mb-2">{doc.fullName}</p>
                    <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">{doc.title}</h3>
                    <p className="text-slate-600 text-sm mb-6">{doc.description}</p>
                    
                    <div className={`${doc.bgColor} rounded-xl p-4 mb-4`}>
                      <p className="text-xs font-medium text-slate-500 mb-3">Ce que l'IA extrait automatiquement</p>
                      <ul className="space-y-2">
                        {doc.extractItems.map((item, i) => (
                          <li key={`item-${i}`} className="flex items-start gap-2 text-sm text-slate-700">
                            <item.icon className={`w-4 h-4 ${doc.textColor} mt-0.5 flex-shrink-0`} />
                            <span>{item.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${doc.textColor}`}>Temps moyen: {doc.time}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-slate-700 text-slate-300">Gain de temps réel</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Ce que vous <span className="italic text-violet-400">regagnez</span> sur chaque marché
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-red-50/10 border-red-200/30 backdrop-blur">
                <CardContent className="p-8">
                  <Badge className="mb-4 bg-red-100 text-red-700">Avant DCE Express</Badge>
                  <div className="text-5xl font-display font-bold text-white mb-2">3 h</div>
                  <p className="text-slate-400 mb-6">par dossier complet</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-slate-300">
                      <FileText className="w-5 h-5 text-red-400 mt-0.5" />
                      Lecture linéaire du RC page par page
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <Target className="w-5 h-5 text-red-400 mt-0.5" />
                      Recherche manuelle des clauses pénalisantes dans le CCAP
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <Wrench className="w-5 h-5 text-red-400 mt-0.5" />
                      Décryptage des normes techniques dans le CCTP
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <ClipboardList className="w-5 h-5 text-red-400 mt-0.5" />
                      Prise de notes et synthèse interne à refaire
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-emerald-50/10 border-emerald-200/30 backdrop-blur">
                <CardContent className="p-8">
                  <Badge className="mb-4 bg-emerald-100 text-emerald-700">Avec DCE Express</Badge>
                  <div className="text-5xl font-display font-bold text-white mb-2">90 s</div>
                  <p className="text-slate-400 mb-6">pour les 3 pièces analysées</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-slate-300">
                      <Zap className="w-5 h-5 text-emerald-400 mt-0.5" />
                      RC synthétisé en 28 secondes — critères et conditions
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <Zap className="w-5 h-5 text-emerald-400 mt-0.5" />
                      CCAP analysé en 31 secondes — alertes financières incluses
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <Zap className="w-5 h-5 text-emerald-400 mt-0.5" />
                      CCTP décrypté en 26 secondes — normes et livrables
                    </li>
                    <li className="flex items-start gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                      Décision de répondre prise en moins de 2 minutes
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="comment-ca-marche" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-violet-100 text-violet-700">Fonctionnement</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Quatre étapes, <span className="italic text-violet-600">moins de 30 secondes</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Aucune installation. Aucune formation. Directement dans votre navigateur, depuis n'importe quel appareil.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <Card className="h-full bg-white border-slate-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl font-display font-bold text-violet-200 mb-4">{step.num}</div>
                    <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600 text-sm">{step.desc}</p>
                  </CardContent>
                </Card>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-violet-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Result Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-slate-100 text-slate-700">Exemple de résultat</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ce que vous obtenez <span className="italic text-violet-600">concrètement</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Un résultat structuré, lisible en 2 minutes, directement exploitable pour préparer votre réponse à l'appel d'offres.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* RC Result */}
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-blue-100 text-blue-700">RC — Points clés extraits</Badge>
                  <Badge className="bg-green-100 text-green-700">Analysé</Badge>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm"><Target className="w-4 h-4 text-blue-500 mt-0.5" /><span><strong>Critères :</strong> Prix 40 % · Valeur technique 40 % · Délai 20 %</span></li>
                  <li className="flex items-start gap-2 text-sm"><Calendar className="w-4 h-4 text-blue-500 mt-0.5" /><span><strong>Date limite de remise :</strong> 15 mars 2025 à 12h00 sur PLACE</span></li>
                  <li className="flex items-start gap-2 text-sm"><FileCheck className="w-4 h-4 text-blue-500 mt-0.5" /><span><strong>Pièces obligatoires :</strong> DC1, DC2, mémoire technique (15 p. max), références similaires</span></li>
                  <li className="flex items-start gap-2 text-sm"><Edit className="w-4 h-4 text-blue-500 mt-0.5" /><span><strong>Variantes :</strong> Non autorisées — offre de base uniquement</span></li>
                  <li className="flex items-start gap-2 text-sm"><Lock className="w-4 h-4 text-blue-500 mt-0.5" /><span><strong>Qualification requise :</strong> Qualibat 2112 ou équivalent</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* CCAP Result */}
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-amber-100 text-amber-700">CCAP — Alertes détectées</Badge>
                  <Badge className="bg-amber-100 text-amber-700">2 alertes</Badge>
                </div>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-2 text-sm"><Euro className="w-4 h-4 text-amber-500 mt-0.5" /><span><strong>Prix :</strong> Ferme et non révisable sur toute la durée</span></li>
                  <li className="flex items-start gap-2 text-sm"><Lock className="w-4 h-4 text-amber-500 mt-0.5" /><span><strong>Avance :</strong> 5 % du montant HT, remboursable par déduction sur acomptes</span></li>
                  <li className="flex items-start gap-2 text-sm"><Shield className="w-4 h-4 text-amber-500 mt-0.5" /><span><strong>Retenue de garantie :</strong> 5 % — libérable après GPA</span></li>
                </ul>
                <div className="space-y-2">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Pénalité de retard : 1/1 000 du montant par jour calendaire — sans plafond contractuel défini
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Résiliation pour convenance du pouvoir adjudicateur possible sans indemnité si délai &gt; 15 jours
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CCTP Result */}
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-emerald-100 text-emerald-700">CCTP — Exigences techniques</Badge>
                  <Badge className="bg-green-100 text-green-700">Analysé</Badge>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm"><Wrench className="w-4 h-4 text-emerald-500 mt-0.5" /><span><strong>Normes applicables :</strong> NF EN 806, NF DTU 60.1, NF DTU 60.11</span></li>
                  <li className="flex items-start gap-2 text-sm"><Hammer className="w-4 h-4 text-emerald-500 mt-0.5" /><span><strong>Matériaux :</strong> Cuivre ou multicouche — PER interdit pour les colonnes montantes</span></li>
                  <li className="flex items-start gap-2 text-sm"><Target className="w-4 h-4 text-emerald-500 mt-0.5" /><span><strong>Essais :</strong> Essai de pression 1,5 × pression de service obligatoire avant réception</span></li>
                  <li className="flex items-start gap-2 text-sm"><Package className="w-4 h-4 text-emerald-500 mt-0.5" /><span><strong>Environnement :</strong> Matériaux avec fiche FDES obligatoire — label Origine France apprécié</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="border-l-4 border-l-violet-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-violet-100 text-violet-700">Actions recommandées</Badge>
                  <Badge className="bg-green-100 text-green-700">4 actions</Badge>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm"><ArrowRight className="w-4 h-4 text-violet-500 mt-0.5" /><span>Vérifier la validité de votre <strong>Qualibat 2112</strong> avant dépôt</span></li>
                  <li className="flex items-start gap-2 text-sm"><ArrowRight className="w-4 h-4 text-violet-500 mt-0.5" /><span>Intégrer le <strong>risque pénalité sans plafond</strong> dans votre calcul de prix</span></li>
                  <li className="flex items-start gap-2 text-sm"><ArrowRight className="w-4 h-4 text-violet-500 mt-0.5" /><span>Préparer la <strong>fiche FDES</strong> pour les matériaux proposés</span></li>
                  <li className="flex items-start gap-2 text-sm"><ArrowRight className="w-4 h-4 text-violet-500 mt-0.5" /><span>Limiter le mémoire technique à <strong>15 pages maximum</strong> (éliminatoire)</span></li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <Badge className="mb-4 bg-violet-100 text-violet-700">FAQ</Badge>
              <h2 className="font-display text-3xl font-bold text-slate-900 mb-4">Questions fréquentes</h2>
              <p className="text-slate-600 mb-6">Tout ce que les PME nous demandent sur la synthèse rapide de pièces de marchés publics.</p>
              <Link to="/contact" className="text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
                Poser une question <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="md:col-span-2 space-y-4">
              {faqItems.map((item, index) => (
                <Card key={`item-${index}`} className="border-slate-200">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 text-left flex items-center justify-between"
                  >
                    <span className="font-medium text-slate-900 pr-4">{item.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 text-slate-600">
                      {item.answer}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-slate-100 text-slate-700">Ils utilisent DCE Express</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ce que disent les PME <span className="italic text-violet-600">sur le terrain</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-slate-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={`item-${i}`} 
                          className={`w-4 h-4 ${i < t.stars ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} 
                        />
                      ))}
                    </div>
                    <blockquote className="text-slate-600 mb-6 italic">"{t.quote}"</blockquote>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-sm">
                        {t.initials}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{t.name}</div>
                        <div className="text-sm text-slate-500">{t.role}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-violet-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Analysez votre première pièce<br />
              <span className="italic text-violet-200">maintenant — c'est gratuit</span>
            </h2>
            <p className="text-violet-100 text-lg mb-8">
              RC, CCAP ou CCTP : déposez votre document et obtenez votre synthèse en 30 secondes. Aucune carte bancaire, aucun engagement.
            </p>
            <Button 
              size="lg"
              onClick={() => user ? navigate("/synthese-marches") : navigate("/analyse-gratuite")}
              className="bg-white text-violet-700 hover:bg-slate-100 rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="cta-final-btn"
            >
              Analyser une pièce gratuitement
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="text-violet-200 text-sm mt-6 uppercase tracking-wide">
              Sans inscription · Résultat en 30 s · Hébergé en France
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-white">Mon DCE Express</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              <Link to="/legal" className="hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <div className="text-sm">
              © 2026 Mon DCE Express. Tous droits réservés.
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default SyntheseRapidePage;
